import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Col, Input, Row, Tooltip } from 'antd'
import { useRolesAndPermissions } from '../../../context/RolesAndPermissionsContext'
import {
  isDuplicateRoleCode,
  isDuplicateRoleName,
} from '../roleFieldUniqueness'
import { Subtitle, CharacterCounter, FieldError } from './styled'

const Inputs = () => {
  const { t } = useTranslation()
  const { id: roleId } = useParams<{ id: string }>()
  const isEditing = Boolean(roleId)
  const {
    roleDetail: data,
    setRoleName,
    setRoleCode,
    setRoleDescription,
    roleOptions: roles,
    roleOptionsLoading: loadingRoles,
    roleOptionsLoaded: loadedRoles,
    fetchRoleOptions,
  } = useRolesAndPermissions()
  const prefix = 'configuration.rolesAndPermissions.form.fields'
  const isCodeDisabled = data.isDefault

  useEffect(() => {
    if (!loadedRoles && !loadingRoles) {
      fetchRoleOptions()
    }
  }, [fetchRoleOptions, loadedRoles, loadingRoles])

  const excludeRoleId = isEditing ? Number(roleId) : undefined

  const duplicateName = useMemo(
    () => isDuplicateRoleName(data.name, roles, excludeRoleId),
    [data.name, roles, excludeRoleId]
  )

  const duplicateCode = useMemo(
    () =>
      isCodeDisabled
        ? false
        : isDuplicateRoleCode(data.code, roles, excludeRoleId),
    [data.code, roles, excludeRoleId, isCodeDisabled]
  )

  const inputErrorStyle = { borderColor: '#f5222d' } as const

  return (
    <>
      <Row gutter={24} style={{ marginTop: '2px' }}>
        <Col span={12}>
          <Subtitle $required>{t(`${prefix}.name`)}</Subtitle>
          <Input
            value={data.name}
            onChange={(event) => setRoleName(event.target.value)}
            style={duplicateName ? inputErrorStyle : undefined}
            autoFocus
          />
          {duplicateName && (
            <FieldError>{t(`${prefix}.errors.duplicateName`)}</FieldError>
          )}
        </Col>
        <Col span={12}>
          <Subtitle $required>
            {t(`${prefix}.code.title`)}
            <Tooltip
              getPopupContainer={() =>
                document.getElementById('lmsPage') as HTMLElement
              }
              title={
                <div style={{ width: '170px' }}>
                  {t(`${prefix}.code.tooltip`)}
                </div>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle as IconProp} />
            </Tooltip>
          </Subtitle>
          <Input
            value={data.code}
            disabled={isCodeDisabled}
            onChange={(event) =>
              setRoleCode(event.target.value.replaceAll(' ', ''))
            }
            style={duplicateCode ? inputErrorStyle : undefined}
          />
          {duplicateCode && (
            <FieldError>{t(`${prefix}.errors.duplicateCode`)}</FieldError>
          )}
        </Col>
      </Row>
      <Subtitle>
        {t(`${prefix}.description.title`)}
        <Tooltip
          getPopupContainer={() =>
            document.getElementById('lmsPage') as HTMLElement
          }
          title={
            <div style={{ width: '130px' }}>
              {t(`${prefix}.description.tooltip`)}
            </div>
          }
        >
          <FontAwesomeIcon icon={faInfoCircle as IconProp} />
        </Tooltip>
      </Subtitle>
      <Input.TextArea
        value={data.description}
        onChange={(event) => setRoleDescription(event.target.value)}
        autoSize={{ maxRows: 2 }}
        maxLength={140}
      />
      <CharacterCounter>
        {data.description.length}/140 {t(`${prefix}.description.characters`)}
      </CharacterCounter>
    </>
  )
}

export default Inputs
